import { OrthographicCamera, Vector3 } from 'three';

import {
    applyOrthographicFrustum,
    getOrthographicVisibleHeight
} from 'app/modules/wallforming/components/scenes/camera-controls/utils/cameraUtils';

import type { CameraControls } from '@react-three/drei';

type Vector3Tuple = [number, number, number];

export interface CameraView {
    position: Vector3Tuple;
    target: Vector3Tuple;
    focalOffset: Vector3Tuple;
    zoom: number;
    up?: Vector3Tuple;
    visibleHeight?: number;
}

const isVector3Tuple = (value: unknown): value is Vector3Tuple =>
    Array.isArray(value) && value.length === 3 && value.every((entry) => Number.isFinite(entry));

export const isCameraView = (value: unknown): value is CameraView => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const view = value as Partial<CameraView>;
    return (
        isVector3Tuple(view.position) &&
        isVector3Tuple(view.target) &&
        isVector3Tuple(view.focalOffset) &&
        typeof view.zoom === 'number' &&
        Number.isFinite(view.zoom) &&
        view.zoom > 0 &&
        (view.up === undefined || isVector3Tuple(view.up)) &&
        (view.visibleHeight === undefined || (Number.isFinite(view.visibleHeight) && view.visibleHeight > 0))
    );
};

export const captureCameraView = (controls: CameraControls): CameraView => ({
    position: controls.getPosition(new Vector3(), false).toArray(),
    target: controls.getTarget(new Vector3(), false).toArray(),
    focalOffset: controls.getFocalOffset(new Vector3(), false).toArray(),
    zoom: controls.camera.zoom,
    up: controls.camera.up.toArray(),
    visibleHeight:
        controls.camera instanceof OrthographicCamera ? getOrthographicVisibleHeight(controls.camera) : undefined
});

export const applyCameraView = (controls: CameraControls, view: CameraView, canvas: HTMLCanvasElement | null) => {
    if (view.up) {
        controls.camera.up.fromArray(view.up);
        controls.updateCameraUp();
    }
    controls.setLookAt(...view.position, ...view.target, false);
    controls.setFocalOffset(...view.focalOffset, false);
    controls.zoomTo(view.zoom, false);
    controls.update(0);

    if (controls.camera instanceof OrthographicCamera && view.visibleHeight) {
        const camera = controls.camera;
        const aspect =
            canvas && canvas.clientHeight > 0
                ? canvas.clientWidth / canvas.clientHeight
                : (camera.right - camera.left) / (camera.top - camera.bottom);
        applyOrthographicFrustum(camera, aspect, view.visibleHeight, controls.distance);
    }
    controls.camera.updateProjectionMatrix();
};
